import React, { createContext, useRef, useContext, useMemo, useState, useCallback } from 'react';
import { useAsyncDisposable } from '../../hooks/useAsyncDisposable';
import { Basic3dLayoutParams, Bootstrapper } from '../domain/Bootstrapper';

type ContextValue = {
  bootstrapper: Bootstrapper | null;
  canvasRef: (node: HTMLCanvasElement | null) => void;
  canvasElement: HTMLCanvasElement | null;
};

const BootstrapperContext = createContext<ContextValue>({
  bootstrapper: null as any,
  canvasRef: () => {},
  canvasElement: null,
});

type Props = {
  children: React.ReactNode;
} & Basic3dLayoutParams;

export const BootstrapperProvider = ({ children, ...rest }: Props) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [canvasReady, setCanvasReady] = useState(false);
  const [canvasElement, setCanvasElement] = useState<HTMLCanvasElement | null>(null);

  // If without3d is true, we don't need to wait for canvas
  const skipCanvas = rest.without3d === true;

  // Callback ref to detect when canvas mounts
  const setCanvasRef = useCallback(
    (node: HTMLCanvasElement | null) => {
      canvasRef.current = node;
      setCanvasElement(node);
      if (node && !canvasReady) {
        setCanvasReady(true);
      }
    },
    [canvasReady],
  );

  const bootstrapper = useAsyncDisposable(async () => {
    // If we need canvas but it's not ready, wait
    if (!skipCanvas && !canvasRef.current) return null;
    const bootstrapper = new Bootstrapper(canvasRef.current!, rest);
    await bootstrapper.init();
    return bootstrapper;
  }, [skipCanvas ? true : canvasReady]);

  const value = useMemo(
    () => ({
      bootstrapper,
      canvasRef: setCanvasRef,
      canvasElement,
    }),
    [bootstrapper, setCanvasRef, canvasElement],
  );

  // Always render children so canvas can mount
  return <BootstrapperContext.Provider value={value}>{children}</BootstrapperContext.Provider>;
};

export const useBootstrapper = () => {
  const context = useContext(BootstrapperContext);
  return context;
};
